import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import TiChevronLeft from 'react-icons/lib/ti/chevron-left';
import TiChevronRight from 'react-icons/lib/ti/chevron-right';
import {
    Swipeable,
} from 'react-swipeable';

import {
    generateIsomorphicID,
} from 'wanderu-utils';

import events from './events.js';

import styles from './Carousel.scss';

function Indicator(props) {
    const {
        index,
        activeIndex,
        activeIndicatorStyle,
        onSelectChild,
    } = props;
    const active = index === activeIndex;

    function _onClick() {
        onSelectChild(index);
    }

    return (
        <li
            className={classNames(
                styles.indicator,
                {
                    [styles.active]: active,
                    [activeIndicatorStyle]: active,
                },
            )}
            key={index}
            onClick={_onClick}
            role="button"
            tabIndex={index}
        >
            &bull;
        </li>
    );
}

Indicator.propTypes = {
    activeIndex: PropTypes.number.isRequired,
    activeIndicatorStyle: PropTypes.string,
    index: PropTypes.number.isRequired,
    onSelectChild: PropTypes.func.isRequired,
};

class Carousel extends React.Component {
    static propTypes = {
        activeIndicatorStyle: PropTypes.string,
        autoplay: PropTypes.bool,
        children: PropTypes.arrayOf(PropTypes.element).isRequired,
        className: PropTypes.string,
        duration: PropTypes.number,
        showArrows: PropTypes.bool,
        showHints: PropTypes.bool,
        showSwitcher: PropTypes.bool,
        wrap: PropTypes.bool,
    }

    static defaultProps = {
        activeIndicatorStyle: '',
        autoplay: false,
        className: '',
        duration: 5000,
        showArrows: false,
        showHints: false,
        showSwitcher: true,
        wrap: false,
    }

    constructor(props) {
        super(props);

        this.state = {
            currentIndex: 0,
        };
        this.autoAdvance = null;
        this.id = generateIsomorphicID({
            ...props,
            children: undefined,
        });
    }

    componentDidMount() {
        const {
            autoplay,
            duration,
        } = this.props;

        if (autoplay) {
            this.autoAdvance = window.setTimeout(this.onAutoAdvance, duration);
        }
    }

    componentWillUnmount() {
        if (this.autoAdvance) {
            window.clearTimeout(this.autoAdvance);
        }
    }

    afterSetIndex = () => {
        const {
            autoplay,
            duration,
        } = this.props;

        if (!autoplay) {
            return;
        }

        window.clearTimeout(this.autoAdvance);

        this.autoAdvance = window.setTimeout(this.onAutoAdvance, duration);
    }

    onAutoAdvance = () => {
        const {
            children,
            wrap,
        } = this.props;
        const {
            currentIndex,
        } = this.state;
        const childCount = React.Children.count(children);

        if (childCount <= 1
            || (!wrap && currentIndex === childCount - 1)) {
            return;
        }

        this.setState(() => {
            return {
                currentIndex: (currentIndex + 1) % childCount,
            };
        }, this.afterSetIndex);
    }

    onSelectChild = (index) => {
        this.setState(() => {
            return {
                currentIndex: index,
            };
        }, this.afterSetIndex);
    }

    onSwipingLeft = () => {
        const {
            children,
            wrap,
        } = this.props;
        const {
            currentIndex,
        } = this.state;
        const childCount = React.Children.count(children);

        if (!wrap && currentIndex === childCount - 1) {
            return;
        }

        this.setState(() => {
            return {
                currentIndex: (currentIndex + 1) % childCount,
            };
        }, this.afterSetIndex);
        events.clickNext();
    }

    onSwipingRight = () => {
        const {
            children,
            wrap,
        } = this.props;
        const {
            currentIndex,
        } = this.state;
        const childCount = React.Children.count(children);

        if (!wrap && currentIndex === 0) {
            return;
        }

        this.setState(() => {
            return {
                currentIndex: (currentIndex + childCount - 1) % childCount,
            };
        }, this.afterSetIndex);
        events.clickPrev();
    }

    handlePrevClick = () => {
        const { children, wrap = false } = this.props;
        const { currentIndex } = this.state;
        const childCount = React.Children.count(children);

        if (currentIndex > 0 || wrap) {
            this.setState(
                { currentIndex: (currentIndex - 1 + childCount) % childCount },
                this.afterSetIndex
            );
        }
        events.clickPrev();
    }

    handleNextClick = () => {
        const { children, wrap = false } = this.props;
        const { currentIndex } = this.state;
        const childCount = React.Children.count(children);

        if (currentIndex < childCount - 1 || wrap) {
            this.setState(
                { currentIndex: (currentIndex + 1) % childCount },
                this.afterSetIndex
            );
        }
        events.clickNext();
    }

    selectSlide = (index) => {
        events.clickSlide({ slideNumber: index });
    }

    render() {
        const {
            activeIndicatorStyle,
            children,
            className,
            showArrows,
            showHints,
            showSwitcher,
            wrap,
        } = this.props;
        const {
            currentIndex,
        } = this.state;
        const childCount = React.Children.count(children);
        const hints = showHints && children.length > 1;
        const effectiveIndex = showHints
            ? currentIndex + 1
            : currentIndex;
        const effectiveLength = showHints
            ? childCount + 2
            : childCount;
        const showLeftArrow = showArrows && (currentIndex > 0 || wrap);
        const showRightArrow = showArrows && (currentIndex < childCount - 1 || wrap);
        const style = {
            transform: `translateX(-${(effectiveIndex / effectiveLength) * 100}%)`,
        };

        return (
            <div className={`${styles.carousel} ${className}`}>
                <Swipeable
                    className={styles.swipeable}
                    delta={50}
                    onSwipedLeft={this.onSwipingLeft}
                    onSwipedRight={this.onSwipingRight}
                >
                    <div
                        aria-live="polite"
                        className={styles.slidingPanel}
                        id={`${this.id}-panel`}
                        style={style}
                    >
                        {hints && (
                            <div
                                aria-hidden="true"
                                className={styles.childContainer}
                                key="wrap-start"
                            >
                                {children[children.length - 1]}
                            </div>
                        )}
                        {children.map((child, idx) => (
                            <div
                                aria-hidden={idx !== currentIndex}
                                className={styles.childContainer}
                                key={idx}
                                onClick={this.selectSlide}
                            >
                                {child}
                            </div>
                        ))}
                        {hints && (
                            <div
                                aria-hidden="true"
                                className={styles.childContainer}
                                key="wrap-end"
                            >
                                {children[0]}
                            </div>
                        )}
                    </div>
                </Swipeable>
                {showLeftArrow && (
                    <button
                        aria-label="Previous slide"
                        className={`${styles.arrowButton} ${styles.leftArrow}`}
                        onClick={this.handlePrevClick}
                    >
                        <TiChevronLeft />
                    </button>
                )}
                {showRightArrow && (
                    <button
                        aria-label="Next slide"
                        className={`${styles.arrowButton} ${styles.rightArrow}`}
                        onClick={this.handleNextClick}
                    >
                        <TiChevronRight />
                    </button>
                )}
                {showSwitcher && children.length > 1 && (
                    <ul
                        aria-controls={`${this.id}-panel`}
                        className={styles.indicators}
                    >
                        {children.map((_, index) => (
                            <Indicator
                                activeIndex={currentIndex}
                                activeIndicatorStyle={activeIndicatorStyle}
                                index={index}
                                key={index}
                                onSelectChild={this.onSelectChild}
                            />
                        ))}
                    </ul>
                )}
            </div>
        );
    }
}

export {
    Carousel,
    Indicator,
};
export default Carousel;
